import { Menu } from "lucide-react";
import nymblLogoWhite from "@/assets/nymbl-logo-white.png";
import { ReportIssueProvider } from "@/components/issues/ReportIssueProvider";
import { Sidebar } from "./Sidebar";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { StatusBar } from "./StatusBar";
import { ViewAsBanner } from "./ViewAsBanner";

function DashboardLayoutInner({ children }: { children: React.ReactNode }) {
  const { isMobile, setMobileOpen } = useSidebar();

  // Mobile: the sidebar collapses into a drawer, so a slim bar carries the only
  // affordance needed to open it (search + user menu live inside the drawer).
  if (isMobile) {
    return (
      <div className="flex h-screen w-screen flex-col overflow-hidden bg-background">
        <ViewAsBanner />
        <header
          className="nymbl-panel flex shrink-0 items-center gap-2 border-b border-sidebar-border bg-sidebar px-3 text-sidebar-foreground"
          style={{ height: "var(--topbar-h)" }}
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="-ml-1 rounded p-1.5 text-white/80 hover:bg-white/10"
            aria-label="Open menu"
          >
            <Menu className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2.5 font-display">
            <img src={nymblLogoWhite} alt="Nymbl" className="h-6 w-auto" />
            <span className="h-4 w-px bg-white/[0.32]" />
            <span className="text-sm font-medium tracking-[0.2em] text-white/[0.82] uppercase">
              Staff
            </span>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
        <StatusBar />
        <Sidebar />
      </div>
    );
  }

  // The View As banner spans the full width above the sidebar, so it can't be missed.
  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-background">
      <ViewAsBanner />
      <div className="flex min-h-0 flex-1">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
          <StatusBar />
        </div>
      </div>
    </div>
  );
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <ReportIssueProvider>
        <DashboardLayoutInner>{children}</DashboardLayoutInner>
      </ReportIssueProvider>
    </SidebarProvider>
  );
}
